import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Users, Search, Ban, CheckCircle2, KeyRound, Trash2, Loader2, AlertTriangle, ChevronLeft, ChevronRight, ShieldCheck, ShieldOff } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { api, apiErr } from "../../lib/api";
import { Loader, Empty, GoldButton, GhostButton } from "../../components/UI";
import { toast } from "sonner";

const STATUS_FILTERS = [
  { key: "ACTIVE", label: "Active" },
  { key: "DEACTIVATED", label: "Deactivated" },
  { key: "DELETED", label: "Deleted" },
  { key: "ALL", label: "All" },
];

const LEGACY_ADMIN_ROLES = ["WSBF_ADMIN", "FEDERATION_ADMIN"];

const STATUS_STYLE = {
  ACTIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  DEACTIVATED: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  DELETED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${STATUS_STYLE[status] || "bg-slate-700/30 text-slate-300 border-slate-600"}`} data-testid={`user-status-${status}`}>
      {status}
    </span>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const tooShort = pw.length < 8;
  const mismatch = confirm.length > 0 && pw !== confirm;
  const submit = async () => {
    if (tooShort || pw !== confirm) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { new_password: pw });
      toast.success("Password reset");
      onDone();
    } catch (e) {
      toast.error(apiErr(e, "Failed to reset password"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="reset-password-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.25)] p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#E5B842]" /> Reset Password
        </h3>
        <p className="text-sm text-slate-400 mt-1 break-words">Set a new password for {user.email}.</p>
        <input type="password" data-testid="reset-password-input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 8 chars)"
          className="w-full mt-4 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
        <input type="password" data-testid="reset-password-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password"
          className="w-full mt-3 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
        {tooShort && pw.length > 0 && <div className="text-xs text-amber-300 mt-2">Password must be at least 8 characters.</div>}
        {mismatch && <div className="text-xs text-rose-300 mt-2">Passwords do not match.</div>}
        <div className="flex justify-end gap-2 mt-4">
          <GhostButton onClick={onClose} data-testid="reset-password-cancel">Cancel</GhostButton>
          <GoldButton onClick={submit} disabled={busy || tooShort || pw !== confirm} data-testid="reset-password-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Reset
          </GoldButton>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ user, onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (confirm !== "DELETE") return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/delete`);
      toast.success("User deleted & anonymized");
      onDone();
    } catch (e) {
      toast.error(apiErr(e, "Failed to delete user"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="delete-user-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0f0f0f] border border-rose-500/30 p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-rose-400" /> Delete User
        </h3>
        <p className="text-sm text-slate-400 mt-2 break-words">
          This permanently anonymizes <span className="text-slate-200">{user.email}</span> (email & name scrubbed, avatar removed) and blocks login. Match/transfer history is preserved. This cannot be undone.
        </p>
        {user.owns_club && (
          <div className="text-xs text-amber-300 mt-3 p-2 rounded bg-amber-500/10 border border-amber-500/20">This user owns a club. The server will block deletion until ownership is transferred.</div>
        )}
        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-4 mb-1.5">Type DELETE to confirm</label>
        <input data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE"
          className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-rose-400/60" />
        <div className="flex justify-end gap-2 mt-4">
          <GhostButton onClick={onClose} data-testid="delete-cancel">Cancel</GhostButton>
          <button onClick={submit} disabled={busy || confirm !== "DELETE"} data-testid="delete-confirm-btn"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed" style={{ transition: "background-color 0.15s ease" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminRoleModal({ target, onClose, onConfirm, busy }) {
  const grant = target.action === "grant-admin";
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="admin-role-modal" onClick={onClose}>
      <div className={`w-full max-w-md rounded-xl bg-[#0f0f0f] border p-6 ${grant ? "border-[rgba(212,175,55,0.35)]" : "border-rose-500/30"}`} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2">
          {grant ? <ShieldCheck className="w-5 h-5 text-[#E5B842]" /> : <ShieldOff className="w-5 h-5 text-rose-400" />}
          {grant ? "Κάνε admin" : "Αφαίρεση admin"}
        </h3>
        <p className="text-sm text-slate-300 mt-2 break-all">{target.user.email}</p>
        <p className="text-sm text-slate-400 mt-2" data-testid="admin-role-modal-consequence">
          {grant
            ? "Ο λογαριασμός θα αποκτήσει ΠΛΗΡΗ πρόσβαση στο Admin Panel: χρήστες, εγκρίσεις συλλόγων, partners και audit log."
            : "Ο λογαριασμός θα χάσει αμέσως την πρόσβαση στο Admin Panel και σε όλα τα admin endpoints."}
        </p>
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-3">Η ενέργεια καταγράφεται στο audit log.</p>
        <div className="flex justify-end gap-2 mt-5">
          <GhostButton onClick={onClose} data-testid="admin-role-cancel">Άκυρο</GhostButton>
          {grant ? (
            <GoldButton onClick={onConfirm} disabled={busy} data-testid="admin-role-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Επιβεβαίωση
            </GoldButton>
          ) : (
            <button onClick={onConfirm} disabled={busy} data-testid="admin-role-confirm"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-40" style={{ transition: "background-color 0.15s ease" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />} Αφαίρεση
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function UserRow({ u, meId, onAction, onAdmin, busyId, onReset, onDelete }) {
  const busy = busyId === u.id;
  const isSelf = u.id === meId;
  const when = u.created_at ? new Date(u.created_at).toLocaleDateString() : "—";
  return (
    <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`user-row-${u.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-heading text-base font-bold uppercase tracking-tight text-slate-50 break-words">{u.display_name || "—"}</span>
          <StatusPill status={u.status} />
          {u.role === "admin" && <span title="Can open the Admin Panel" className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#E5B842]/40 bg-[#1a1407] text-[#E5B842]" data-testid={`user-admin-chip-${u.id}`}>admin</span>}
          {(u.roles || []).filter((r) => LEGACY_ADMIN_ROLES.includes(r)).map((r) => (
            <span key={r} title="Legacy role, no Admin Panel access" className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-slate-600/50 text-slate-500" data-testid={`user-legacy-chip-${u.id}-${r.toLowerCase()}`}>legacy · {r}</span>
          ))}
          {isSelf && <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">you</span>}
        </div>
        <div className="text-xs text-slate-400 font-mono mt-1 break-all">{u.email}</div>
        <div className="text-xs text-slate-500 font-mono mt-1 break-words">
          {u.wsbf_player_id || "—"}
          {u.club && <><span className="text-slate-600"> · </span>{u.club.name}</>}
          <span className="text-slate-600"> · </span>VP: <span className="text-[#E5B842]" data-testid={`user-vp-${u.id}`}>{u.vp_balance ?? "—"}</span>
          <span className="text-slate-600"> · </span>{when}
        </div>
      </div>
      {!isSelf && u.status !== "DELETED" && (
        <div className="flex flex-wrap gap-2 shrink-0">
          {u.status === "ACTIVE" ? (
            <GhostButton onClick={() => onAction(u, "deactivate")} disabled={busy} data-testid={`deactivate-user-${u.id}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Deactivate
            </GhostButton>
          ) : (
            <GoldButton onClick={() => onAction(u, "activate")} disabled={busy} data-testid={`activate-user-${u.id}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Activate
            </GoldButton>
          )}
          <GhostButton onClick={() => onReset(u)} data-testid={`reset-user-${u.id}`}><KeyRound className="w-4 h-4" /> Reset PW</GhostButton>
          {u.role === "admin" ? (
            <GhostButton onClick={() => onAdmin(u, "revoke-admin")} disabled={busy} data-testid={`revoke-admin-${u.id}`}>
              <ShieldOff className="w-4 h-4" /> Αφαίρεση admin
            </GhostButton>
          ) : u.status === "ACTIVE" && (
            <GhostButton onClick={() => onAdmin(u, "grant-admin")} disabled={busy} data-testid={`grant-admin-${u.id}`}>
              <ShieldCheck className="w-4 h-4" /> Κάνε admin
            </GhostButton>
          )}
          {u.role !== "admin" && (
            <button onClick={() => onDelete(u)} data-testid={`delete-user-${u.id}`}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-rose-500/40 text-rose-300 hover:border-rose-400 hover:text-rose-200" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminUsers() {
  const [sp] = useSearchParams();
  const initStatus = (sp.get("status") || "").toUpperCase();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(STATUS_FILTERS.some((f) => f.key === initStatus) ? initStatus : "ACTIVE");
  const [search, setSearch] = useState("");
  const [adminsOnly, setAdminsOnly] = useState(sp.get("admins") === "1");
  const [busyId, setBusyId] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [meId, setMeId] = useState(null);
  const PAGE_SIZE = 20;

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMeId(data.user?.id)).catch(() => {});
  }, []);

  const reqSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setRows(null);
    try {
      const { data } = await api.get("/admin/users", { params: { status, search: search || undefined, page, page_size: PAGE_SIZE, admins_only: adminsOnly || undefined } });
      if (seq !== reqSeq.current) return; // stale: filters changed while in flight
      setRows(data.users || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      toast.error(apiErr(e, "Failed to load users"));
      setRows([]);
    }
  }, [status, search, page, adminsOnly]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  useEffect(() => { setPage(1); }, [status, search, adminsOnly]);

  const doAction = async (u, action) => {
    if (!window.confirm(`${action === "deactivate" ? "Deactivate" : "Activate"} ${u.email}?`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/${action}`);
      toast.success(`User ${action}d`);
      load();
    } catch (e) {
      toast.error(apiErr(e, `Failed to ${action} user`));
    } finally {
      setBusyId(null);
    }
  };

  const [roleTarget, setRoleTarget] = useState(null);
  const doAdmin = (u, action) => setRoleTarget({ user: u, action });
  const confirmAdmin = async () => {
    const { user: u, action } = roleTarget;
    const grant = action === "grant-admin";
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/${action}`);
      toast.success(grant ? `${u.email} is now an admin` : `Admin removed from ${u.email}`);
      setRoleTarget(null);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Failed to update admin role"));
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminLayout>
      <div data-testid="admin-users-page">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2">Control Center</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none flex items-center gap-3">
            <Users className="w-8 h-8 text-[#E5B842] shrink-0" /> Users
          </h1>
          <p className="text-slate-400 mt-2 text-sm sm:text-base">{total} account{total === 1 ? "" : "s"}.</p>
        </div>

        <div className="relative mb-4">
          <Search className="w-4 h-4 text-[#E5B842]/70 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input data-testid="users-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email, name or WSTF player ID"
            className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-xl pl-11 pr-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="users-filters">
          {STATUS_FILTERS.map((f) => (
            <button key={f.key} onClick={() => setStatus(f.key)} data-testid={`users-filter-${f.key.toLowerCase()}`}
              className={`px-4 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wide ${status === f.key ? "bg-[#E5B842] text-black" : "bg-[#161616] border border-[rgba(212,175,55,0.22)] text-slate-300 hover:text-[#E5B842]"}`}
              style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
              {f.label}
            </button>
          ))}
          <span className="w-px bg-[rgba(212,175,55,0.22)] mx-1" />
          <button onClick={() => setAdminsOnly((v) => !v)} data-testid="users-filter-admins-only" aria-pressed={adminsOnly}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wide ${adminsOnly ? "bg-[#E5B842] text-black" : "bg-[#161616] border border-[rgba(212,175,55,0.22)] text-slate-300 hover:text-[#E5B842]"}`}
            style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Μόνο admins
          </button>
        </div>

        {rows === null ? (
          <Loader label="Loading users…" />
        ) : rows.length === 0 ? (
          <Empty label="No users found" />
        ) : (
          <>
            <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)] divide-y divide-[rgba(212,175,55,0.10)]" data-testid="users-list">
              {rows.map((u) => (
                <UserRow key={u.id} u={u} meId={meId} onAction={doAction} onAdmin={doAdmin} busyId={busyId} onReset={setResetting} onDelete={setDeleting} />
              ))}
            </div>
            <div className="flex items-center justify-between mt-5">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Page {page} / {totalPages}</div>
              <div className="flex gap-2">
                <GhostButton data-testid="users-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}><ChevronLeft className="w-4 h-4" /> Prev</GhostButton>
                <GhostButton data-testid="users-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next <ChevronRight className="w-4 h-4" /></GhostButton>
              </div>
            </div>
          </>
        )}
      </div>

      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onDone={() => { setResetting(null); load(); }} />}
      {deleting && <DeleteModal user={deleting} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); load(); }} />}
      {roleTarget && <AdminRoleModal target={roleTarget} busy={busyId === roleTarget.user.id} onClose={() => setRoleTarget(null)} onConfirm={confirmAdmin} />}
    </AdminLayout>
  );
}
